import { createFileRoute } from '@tanstack/react-router';
import { Alert, Button, Card, Group, PasswordInput, Progress, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { z } from 'zod';
import { notifications } from '@mantine/notifications';
import { IconFingerprint, IconUserShield } from '@tabler/icons-react';
import { useState } from 'react';
import * as authApi from '@/features/auth/api';
import { passwordSchema, computeStrength } from '@/features/auth/password-utils';
import { md5 } from '@/shared/lib/md5';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { createPasskey, isUserCancelled, isWebAuthnAvailable } from '@/shared/lib/webauthn';
import { useAuth } from '@/app/providers/AuthProvider';

export const Route = createFileRoute('/_app/settings/security')({ component: SecurityPage });

const schema = z
  .object({
    oldPassword: z.string().min(1, 'Obbligatoria'),
    newPassword: passwordSchema,
    confirm: z.string(),
  })
  .refine((d) => d.newPassword === d.confirm, { path: ['confirm'], message: 'Le password non coincidono' });

function SecurityPage() {
  const [busy, setBusy] = useState(false);
  const { decoded } = useAuth();
  // BE (post-v1.9): durante l'impersonation il cambio password è vietato (403,
  // a prescindere dal body). Disabilitiamo il form per evitare un errore certo.
  const isImpersonating = Boolean((decoded as Record<string, unknown> | null)?.impersonated_by);
  const form = useForm({
    mode: 'controlled',
    initialValues: { oldPassword: '', newPassword: '', confirm: '' },
    validate: zodResolver(schema),
  });
  const strength = computeStrength(form.values.newPassword);

  const onSubmit = form.onSubmit(async (vals) => {
    setBusy(true);
    try {
      await authApi.changePassword(md5(vals.oldPassword), md5(vals.newPassword));
      notifications.show({ color: 'green', message: 'Password aggiornata' });
      form.reset();
    } catch (err) {
      const msg = err instanceof JsonApiHttpError ? (err.errors[0]?.detail ?? 'Errore') : err instanceof Error ? err.message : 'Errore';
      notifications.show({ color: 'red', title: 'Modifica fallita', message: msg });
    } finally {
      setBusy(false);
    }
  });

  return (
    <Stack>
      <Title order={2}>Sicurezza</Title>
      <Card withBorder padding="lg" radius="md">
        <form onSubmit={onSubmit}>
          <Stack>
            <Title order={4}>Cambia password</Title>
            {isImpersonating && (
              <Alert color="orange" variant="light" icon={<IconUserShield size={18} />}>
                Stai impersonando un altro utente: il cambio password non è consentito in questa modalità.
              </Alert>
            )}
            <PasswordInput label="Password attuale" required autoComplete="current-password" disabled={isImpersonating} {...form.getInputProps('oldPassword')} />
            <PasswordInput label="Nuova password" required autoComplete="new-password" disabled={isImpersonating} {...form.getInputProps('newPassword')} />
            {form.values.newPassword.length > 0 && (
              <div>
                <Progress value={(strength.score / 4) * 100} color={strength.color} size="sm" />
                <Text size="xs" c={strength.color} mt={4}>
                  Robustezza: {strength.label}
                </Text>
              </div>
            )}
            <PasswordInput label="Conferma nuova password" required disabled={isImpersonating} {...form.getInputProps('confirm')} />
            <Group justify="flex-end">
              <Button type="submit" loading={busy} disabled={isImpersonating}>Aggiorna</Button>
            </Group>
          </Stack>
        </form>
      </Card>
      <PasskeySection />
    </Stack>
  );
}

/**
 * Registrazione passkey (BE v1.9+): begin → navigator.credentials.create → finish.
 * Il BE non espone (ancora) lista/revoca delle passkey: solo aggiunta.
 */
function PasskeySection() {
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');

  if (!isWebAuthnAvailable()) return null;

  const addPasskey = async () => {
    setBusy(true);
    try {
      const displayName = name.trim() || undefined;
      const begin = await authApi.passkeyRegisterBegin(displayName);
      const credentialJson = await createPasskey(begin.optionsJson);
      await authApi.passkeyRegisterFinish(credentialJson, begin.state, displayName);
      notifications.show({ color: 'green', message: 'Passkey registrata. Puoi usarla per accedere dal login.' });
      setName('');
    } catch (err) {
      if (isUserCancelled(err)) return;
      const msg = err instanceof JsonApiHttpError ? err.getUserMessage() : err instanceof Error ? err.message : 'Errore';
      notifications.show({ color: 'red', title: 'Registrazione passkey fallita', message: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack>
        <div>
          <Title order={4}>
            <IconFingerprint size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Passkey
          </Title>
          <Text size="sm" c="dimmed">
            Accedi senza password con Face ID, Touch ID, Windows Hello o una security key.
            La passkey vale per questo dominio su tutti i tuoi dispositivi sincronizzati.
          </Text>
        </div>
        <Group align="flex-end">
          <TextInput
            label="Nome del dispositivo"
            placeholder="es. Il mio laptop"
            description="Facoltativo, ti aiuta a riconoscerla."
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            style={{ flex: 1 }}
            maw={320}
          />
          <Button leftSection={<IconFingerprint size={16} />} loading={busy} onClick={() => void addPasskey()}>
            Aggiungi passkey
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
